import { Component, NgZone, OnInit } from '@angular/core';
import {
  getNspInternetValues,
  getNspMobileValues,
  getNspProductsInPack,
  getNspProductsInPackPending,
  getNspProductsInPackTechnicalError,
  getNspStandaloneProducts,
  getNspStandaloneProductsPending,
  getNspStandaloneTechnicalError,
} from '../../store/reducers';
import { Product } from '@dch/products-business-logic';
import { getPiiNsp } from '../../shared/pii/pii.utils';
import {
  GetNspProductsInPack,
  GetNspStandaloneProducts,
} from '../store/actions/nsp.actions';
import { OnDestroyHookDirective } from '../../shared/hooks';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  AnalyticsEventData,
  AnalyticsService,
  InceptionService,
  TranslationService,
} from '@proximus/ng-common';
import {
  buildWebshopOrderInfo,
  getNspInternetProducts,
  getNspMobileProducts,
} from '../utils/nsp.utils';
import { NspBasketModalComponent } from '../nsp-basket-modal/nsp-basket-modal.component';
import {
  LevelType,
  MessageType,
  ModalService,
  PxModalMessageService,
} from '@proximus/ng-common-components';
import { WebshopUtils } from '../../shared/webshop/webshop-utils';
import { combineLatest } from 'rxjs';
import { AlwaysProtectedCheckService } from "../../services/always-protected-check.service";

@Component({
  selector: 'mprd-nsp-manage-container',
  templateUrl: './nsp-manage-container.component.html',
})
export class NspManageContainerComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  internetProducts: Product[];
  mobileProducts: Product[];
  productsLoaded = false;
  pending = false;
  error = false;
  hasChanged = false;
  isStandalone = false;
  isCancelling = false;
  isAlwaysProtected = false;
  constructor(
    private router: Router,
    private store: Store,
    private inceptionService: InceptionService,
    private modalService: ModalService,
    private ngZone: NgZone,
    private translationService: TranslationService,
    private pxModalMessageService: PxModalMessageService,
    private analyticsService: AnalyticsService,
    private alwaysProtectedService: AlwaysProtectedCheckService
  ) {
    super();
    this.modalCTAConfirm = this.modalCTAConfirm.bind(this);
  }

  ngOnInit() {
    this.subscribeToWebshopEvents();
    this.addSubscription(
      this.store.select(getNspProductsInPack).subscribe((products) => {
        this.internetProducts = getNspInternetProducts(products);
        this.mobileProducts = getNspMobileProducts(products);
        this.productsLoaded = products && products.length > 0;
      }),
    );
    // always protected value from service
    this.alwaysProtectedService.currentAlwaysProtected.subscribe(data => {
      this.isAlwaysProtected = data;
    })

    this.addSubscription(
      this.store.select(getNspProductsInPackPending).subscribe((pending) => {
        this.pending = pending;
      }),
    );
    this.addSubscription(
      this.store
        .select(getNspProductsInPackTechnicalError)
        .subscribe((error) => {
          this.error = !!error;
        }),
    );
    this.addSubscription(
      this.store.select(getNspStandaloneProducts).subscribe((products) => {
        this.internetProducts = getNspInternetProducts(products);
        this.mobileProducts = getNspMobileProducts(products);
        this.productsLoaded = products && products.length > 0;
      }),
    );
    this.addSubscription(
      this.store
        .select(getNspStandaloneProductsPending)
        .subscribe((pending) => {
          this.pending = pending;
        }),
    );
    this.addSubscription(
      this.store.select(getNspStandaloneTechnicalError).subscribe((error) => {
        this.error = !!error;
      }),
    );

    const { packId, productId, plcList } = getPiiNsp();
    if (packId) {
      this.store.dispatch(new GetNspProductsInPack({ packId }));
      this.isStandalone = false;
    } else if (productId || plcList) {
      this.isStandalone = true;
      this.store.dispatch(new GetNspStandaloneProducts({ productId, plcList }));
    } else {
      this.router.navigate(['/overview']);
    }


    this.scrollToTop();

    this.inceptionService.init();
  }

  handleHasChanged(hasChanged: boolean) {
    this.hasChanged = hasChanged;
  }

  scrollToTop(): void {
    setTimeout(() => {
      this.inceptionService.scrollToElement('#nspManageContainer');
    });
  }

  modalCTAConfirm(): void {
    this.addSubscription(
      combineLatest([
        this.store.select(getNspInternetValues),
        this.store.select(getNspMobileValues),
      ]).subscribe((result: any) => {
        const internetValues = result[0];
        const mobileValues = result[1];
        this.isCancelling = true;
        WebshopUtils.manageNspProducts(
          buildWebshopOrderInfo(internetValues, mobileValues, true),
        );
      }),
    );
  }

  confirmChanges(): void {
    this.modalService
      .create<NspBasketModalComponent>(NspBasketModalComponent)
      .subscribe((componentRef) => {
        componentRef.instance.isStandalone = this.isStandalone;
        componentRef.instance.isActivationFlow = false;
      });
  }

  private subscribeToWebshopEvents() {
    WebshopUtils.subscribeToConfirmationEvent((data: any) => {
      this.ngZone.run(() => {
        if (
          this.isCancelling &&
          data.productDetails &&
          data.action &&
          data.action === 'remove'
        ) {
          this.pxModalMessageService.dispatchMessage(
            MessageType.Confirm,
            LevelType.Root,
            this.translationService.getTranslatedValueFor(
              'nsp.basket.cancel.success.message',
            ),
            this.translationService.getTranslatedValueFor(
              'nsp.basket.cancel.success.title',
            ),
            false,
            true,
            'nspOrderSuccess',
            false,
            () => this.onModalMessageClose(),
          );
          this.sendTaggingEvent({
            event: 'popup-impression',
            popup: { zone: 'nsp', name: 'deactivation success' },
            action: { name: 'order' },
            product: [this.mobileProducts, this.internetProducts],
          });
        }
      });
    }, this.addSubscription);

    WebshopUtils.subscribeToErrorEvent((data: any) => {
      this.ngZone.run(() => {
        if (
          this.isCancelling &&
          data.products &&
          data.action &&
          data.action === 'remove'
        ) {
          if (
            data.httpCode &&
            data.httpCode === 402 &&
            data.message &&
            data.message === 'shoppingcart.ccw.validation.failed.customer.ibl'
          ) {
            this.pxModalMessageService.dispatchMessage(
              MessageType.Error,
              LevelType.Root,
              this.translationService.getTranslatedValueFor(
                'message.error.description.cwc',
              ),
              this.translationService.getTranslatedValueFor(
                'message.error.title',
              ),
              false,
              true,
              'nspOrderErrorCwc',
            );
            this.sendTaggingEvent({
              event: 'popup-impression',
              popup: { zone: 'nsp', name: 'order error cwc' },
              action: { name: 'order' },
              product: [this.mobileProducts, this.internetProducts],
            });
          } else {
            this.pxModalMessageService.dispatchMessage(
              MessageType.Error,
              LevelType.Root,
              this.translationService.getTranslatedValueFor(
                'nsp.basket.order.error.message',
              ),
              this.translationService.getTranslatedValueFor(
                'message.error.title',
              ),
              false,
              true,
              'nspOrderError',
            );
            this.sendTaggingEvent({
              event: 'popup-impression',
              popup: { zone: 'nsp', name: 'order error' },
              action: { name: 'order' },
              product: [this.mobileProducts, this.internetProducts],
            });
          }
          this.isCancelling = false;
        }
      });
    }, this.addSubscription);
  }

  private onModalMessageClose() {
    if (this.isCancelling) {
      this.isCancelling = false;
      this.router.navigate(['/overview']);
    }
  }

  private sendTaggingEvent(data: AnalyticsEventData) {
    this.analyticsService.reportEvent(data);
  }
}
